import React, { FC, forwardRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { getButtonStyles } from '../utils/getButtonStyles';
import { IconLink, IconLinkProps } from './IconLink';
import { ThemeButtonKeys } from '../styles/tokens/buttons';
import { getOffsetSpace } from '../utils/getOffsetSpace';
import { space } from '../styles/tokens';
import { getResponsiveType } from '../utils/getResponsiveType';
import { fontWeight } from '../styles/tokens/typography';

const rotateAni = keyframes`
  0% {
    transform: translatex(-50%) translatey(-50%) rotate(0deg);
  }
  100% {
    transform: translatex(-50%) translatey(-50%) rotate(360deg);
  }
  `;

export interface ButtonProps extends IconLinkProps {
  variant?: ThemeButtonKeys;
  selected?: boolean;
  rel?: string;
  role?: string;
  small?: boolean;
  submitSpacing?: boolean;
  isLoading?: boolean;
}

interface StyledButtonProps {
  variant?: ThemeButtonKeys;
  selected?: boolean;
  disabled?: boolean;
  small?: boolean;
  submitSpacing?: boolean;
  isLoading?: boolean;
}

const spaceMd = getOffsetSpace(space.md);
const spaceXxl = getOffsetSpace(space.xxl);

export const StyledButton = styled(IconLink)<StyledButtonProps>`
  ${({ theme: { space, global, components, maxMedia }, variant, selected, disabled, small, submitSpacing, isLoading, iconOnly }) => {
    let padding = `${spaceMd} ${spaceXxl}`;

    if (small) {
      padding = `${space.sm} ${space.md}`;
    }

    if (iconOnly) {
      padding = '8px';
    }


    return css`
    text-decoration: none;
    padding: ${padding};
    border-radius: ${global.borderRadius.default};
    ${getResponsiveType('fieldInput')}
    font-weight: ${fontWeight.bold};
    justify-content: center;
    cursor: pointer;
    align-items: center;
    text-transform: ${components.buttons[variant || 'solid'].uppercase ? `uppercase` : `none`};
    background-color: ${components.buttons[variant || 'solid'].background};
    border: solid 2px ${components.buttons[variant || 'solid'].border};
    color: ${components.buttons[variant || 'solid'].color};

    &:hover {
      background-color: ${components.buttons[variant || 'solid'].hover.background};
      border: solid 2px ${components.buttons[variant || 'solid'].hover.border};
      color: ${components.buttons[variant || 'solid'].hover.color};
      text-decoration: ${components.buttons[variant || 'solid'].hover.underline ? `underline ` : `none`};
    }

    ${disabled &&
    css`
      cursor: default;
      background-color: ${components.buttons[variant || 'solid'].disabled.background};
      border: solid 2px ${components.buttons[variant || 'solid'].disabled.border};
      color: ${components.buttons[variant || 'solid'].disabled.color};

      &:hover {
        background-color: ${components.buttons[variant || 'solid'].disabled.background};
        border: solid 2px ${components.buttons[variant || 'solid'].disabled.border};
        color: ${components.buttons[variant || 'solid'].disabled.color};
        text-decoration: none;
      }
    `}

    ${isLoading &&
    css`
      position: relative;
      transition: all 0.3s linear;
      &:before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translatex(-50%) translatey(-50%);
        display: inline-block;
        width: ${space.lg};
        height: ${space.lg};
        border: 2px solid;
        border-radius: 50%;
        border-top-color: rgba(255, 255, 255, 0.7);
        border-left-color: rgba(255, 255, 255, 0.1);
        border-right-color: rgba(255, 255, 255, 0.5);
        border-bottom-color: rgba(255, 255, 255, 0.3);
        animation: ${rotateAni} 0.6s linear infinite;
        display: block;
      }
      span {
        color: transparent;
      }
    `}

    ${small &&
    css`
      padding: ${padding};;
      gap: ${space.xs};
      max-height: 44px;
    `}

    ${submitSpacing &&
    css`
      margin-top: ${space.xxl};
    `}
  `;
  }}
`;

export const Button: FC<React.PropsWithChildren<ButtonProps>> = forwardRef(
  (
    {
      children,
      a11yTitle,
      role,
      id,
      className,
      onClick,
      disabled = false,
      variant = 'solid',
      as,
      url,
      target,
      label,
      type,
      arrowRight,
      arrowLeft,
      iconLeft,
      iconRight,
      rel,
      selected,
      small,
      isLoading,
      submitSpacing,
      hideLabelOnMobile,
      state,
      ...rest
    },
    ref
  ) => {
    // if (!label && !children) {
    //   return null;
    // }

    const iconOnly: boolean =
      (!!arrowLeft || !!arrowRight || !!iconRight || !!iconLeft) &&
      !label &&
      !children;

    const handleClick = (...props) => {
      if (onClick) {
        onClick(...props);
      }
    };

    return (
      <StyledButton
        ref={ref}
        id={id}
        className={className}
        url={url}
        onClick={handleClick}
        aria-label={a11yTitle}
        role={role}
        type={type}
        variant={variant}
        disabled={disabled || isLoading}
        aria-disabled={disabled || isLoading}
        target={target && '__blank'}
        {...rest}
        arrowLeft={arrowLeft}
        iconLeft={iconLeft}
        iconOnly={iconOnly}
        label={label}
        arrowRight={arrowRight}
        iconRight={iconRight}
        arrowLeftBack
        rel={rel}
        selected={selected}
        hideLabelOnMobile={hideLabelOnMobile}
        as={as}
        state={state}
        small={small}
        isLoading={isLoading}
        submitSpacing={submitSpacing}
        eventName="cta button"
      >
        {children || label}
      </StyledButton>
    );
  }
);
